import type { ChapterEnum } from 'gql/graphql';
import { CostGroupEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { Position } from 'uikit';
import { Tooltip, Button, cnMixSpace } from 'uikit';

import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AddOrderOtherToAktKC2 } from './AddOrderOtherToAktKC2';
import { AddOthersTable } from './AddOthersTable';

export const AddOthers = ({
    workOrderId,
    onChange,
    constructionTypeId,
    isDisabled,
    chapter,
    workOrderStatus,
    workOrderObjectId,
    isOpenedOuter = false,
    closeOuterHandler,
    withBtn = true,
    tooltipPosition,
    tooltipOuterOpened,
    isNotChosenFiltersOther,
}: {
    workOrderId: number;
    onChange: () => void;
    constructionTypeId: number;
    isDisabled: boolean;
    chapter: ChapterEnum;
    workOrderObjectId: number;
    workOrderStatus?: WorkOrderStatusEnum;
    isOpenedOuter?: boolean;
    withBtn?: boolean;
    tooltipPosition?: Position;
    tooltipOuterOpened?: boolean;
    isNotChosenFiltersOther?: boolean;
    closeOuterHandler?: () => void;
}) => {
    const { id: projectId, orderId } = useParams();
    const buttonRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const isStatusWorkOrderOnFormation = workOrderStatus === WorkOrderStatusEnum.OnFormation;
    const isStatusWorkOrderInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;

    const handleOnButtonClick = useCallback(() => {
        setIsOpen(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_NZ_add other button_pressed',
            projectId,
            orderId: Number(orderId),
            addType: CostGroupEnum.Others,
            orderStatus: workOrderStatus,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        workOrderStatus,
    ]);

    const handleOnEditCancel = useCallback(() => {
        if (closeOuterHandler) {
            closeOuterHandler();
        } else {
            setIsOpen(false);
        }
    }, [closeOuterHandler]);

    const handleOnEditConfirm = useCallback(() => {
        handleOnEditCancel();
        onChange();
    }, [handleOnEditCancel, onChange]);

    return (
        <>
            {withBtn && (
                <Button
                    className={cnMixSpace({ mR: 'xs' })}
                    label="Добавить прочие"
                    view="secondary"
                    size="s"
                    onClick={handleOnButtonClick}
                    disabled={isDisabled}
                    ref={buttonRef}
                />
            )}
            {(isOpenedOuter || isOpen) && isStatusWorkOrderOnFormation && (
                <AddOthersTable
                    onConfirm={handleOnEditConfirm}
                    onCancel={handleOnEditCancel}
                    constructionTypeId={constructionTypeId}
                    workOrderId={workOrderId}
                    chapter={chapter}
                />
            )}
            {(isOpenedOuter || isOpen) && isStatusWorkOrderInProgress && (
                <AddOrderOtherToAktKC2
                    onConfirm={handleOnEditConfirm}
                    onCancel={handleOnEditCancel}
                    constructionTypeId={constructionTypeId}
                    workOrderObjectId={workOrderObjectId}
                />
            )}
            {tooltipOuterOpened && (
                <Tooltip
                    size="s"
                    direction="rightCenter"
                    anchorRef={buttonRef}
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {isNotChosenFiltersOther
                        ? 'Выберите раздел "Прочие" и объект'
                        : 'В НЗ в статусе "В работе" добавление прочих доступно через создание Акта КС-2'}
                </Tooltip>
            )}
        </>
    );
};

AddOthers.displayName = 'AddOthers';
